import React from 'react';
import { motion } from 'framer-motion';
import { FileWarning, Database, Layers, AlertCircle } from 'lucide-react';

export const Problem: React.FC = () => {
  return (
    <section id="problem" className="py-24 bg-gray-50/50 relative overflow-hidden">
       {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-white to-transparent"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-50 text-red-600 text-xs font-semibold uppercase tracking-wide mb-6">
                <AlertCircle size={14} />
                The Current Challenge
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-6 leading-tight">
              Data is fragmented.<br/>Decisions are delayed.
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Real estate developers and consultants currently lose hours manually cross-referencing data. 
              Critical information is scattered across disconnected government portals, leading to high feasibility risks.
            </p>
            
            <div className="space-y-6">
                <div className="flex gap-4">
                    <div className="w-12 h-12 rounded-lg bg-white border border-gray-100 shadow-sm flex items-center justify-center flex-shrink-0">
                         <Database className="text-orange-500" size={24} />
                    </div>
                    <div>
                        <h4 className="font-bold text-gray-900">Siloed Ownership Data</h4>
                        <p className="text-sm text-gray-500 mt-1">AnyRoR records are text-based and disconnected from maps.</p>
                    </div>
                </div>
                <div className="flex gap-4">
                    <div className="w-12 h-12 rounded-lg bg-white border border-gray-100 shadow-sm flex items-center justify-center flex-shrink-0">
                         <Layers className="text-blue-500" size={24} />
                    </div>
                    <div>
                        <h4 className="font-bold text-gray-900">Static Town Planning Maps</h4>
                        <p className="text-sm text-gray-500 mt-1">AUDA/AMC maps are often PDF-based or hard to overlay.</p>
                    </div>
                </div>
            </div>
          </motion.div>

          {/* Illustration */}
          <div className="relative h-[500px] w-full flex items-center justify-center">
             {/* Background Circle */}
             <div className="absolute w-96 h-96 bg-white rounded-full shadow-2xl shadow-gray-200/50 z-0"></div>
             
             {/* Floating Silos */}
             <motion.div 
                animate={{ y: [-10, 10, -10] }}
                transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
                className="absolute top-20 left-10 md:left-20 z-10 bg-white p-6 rounded-2xl shadow-lg border border-gray-100 w-48"
             >
                 <div className="h-2 w-1/2 bg-orange-200 rounded mb-2"></div>
                 <div className="text-lg font-bold text-gray-800 mb-2">AnyRoR</div>
                 <div className="space-y-2">
                     <div className="h-1 w-full bg-gray-100 rounded"></div>
                     <div className="h-1 w-3/4 bg-gray-100 rounded"></div>
                 </div>
                 <div className="absolute -top-2 -right-2 bg-red-100 text-red-600 p-1 rounded-full border border-white">
                     <FileWarning size={16} />
                 </div>
             </motion.div>

             <motion.div 
                animate={{ y: [15, -15, 15] }}
                transition={{ repeat: Infinity, duration: 6, ease: "easeInOut", delay: 1 }}
                className="absolute bottom-32 right-10 md:right-20 z-20 bg-white p-6 rounded-2xl shadow-lg border border-gray-100 w-48"
             >
                 <div className="h-2 w-1/2 bg-blue-200 rounded mb-2"></div>
                 <div className="text-lg font-bold text-gray-800 mb-2">TP Maps</div>
                 <div className="aspect-video bg-blue-50 rounded mb-2 border border-blue-100 dashed"></div>
             </motion.div>

             <motion.div 
                animate={{ y: [-5, 5, -5] }}
                transition={{ repeat: Infinity, duration: 4, ease: "easeInOut", delay: 0.5 }}
                className="absolute bottom-10 left-20 md:left-32 z-30 bg-white p-6 rounded-2xl shadow-lg border border-gray-100 w-48"
             >
                 <div className="h-2 w-1/2 bg-purple-200 rounded mb-2"></div>
                 <div className="text-lg font-bold text-gray-800 mb-2">GDCR</div>
                 <div className="space-y-2">
                     <div className="h-1 w-full bg-gray-100 rounded"></div>
                     <div className="h-1 w-full bg-gray-100 rounded"></div>
                 </div>
             </motion.div>
             
             {/* Central Disconnect */}
             <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0 opacity-20">
                 <svg width="400" height="400" viewBox="0 0 400 400">
                     <path d="M100 100 L300 300 M300 100 L100 300" stroke="#94a3b8" strokeWidth="2" strokeDasharray="8 8" />
                 </svg>
             </div>
          </div>

        </div>
      </div>
    </section>
  );
};
